import React, { useEffect, useState, useCallback } from 'react';
import {
  SpaceBetween, Box, Input, Select, Button, Header, Spinner, Badge, ColumnLayout, Alert,
} from '@cloudscape-design/components';
import { browse } from './browseClient';
import AgentRevealBanner from './AgentRevealBanner';

/** Cart facade over the LIFTED session store (so cart/orders persist across session
 *  switches). `lines` is owned by the parent; mutations call setLines(updater). */
function useCart(lines, setLines) {
  const add = (p) => setLines((ls) => {
    const i = ls.findIndex((l) => l.product.sku === p.sku);
    if (i >= 0) { const n = [...ls]; n[i] = { ...n[i], qty: n[i].qty + 1 }; return n; }
    return [...ls, { product: p, qty: 1 }];
  });
  const setQty = (sku, qty) => setLines((ls) => ls.map((l) => l.product.sku === sku ? { ...l, qty: Math.max(1, qty) } : l));
  const remove = (sku) => setLines((ls) => ls.filter((l) => l.product.sku !== sku));
  const clear = () => setLines([]);
  const count = lines.reduce((n, l) => n + l.qty, 0);
  const subtotal = lines.reduce((s, l) => s + parseFloat(l.product.price) * l.qty, 0);
  return { lines, add, setQty, remove, clear, count, subtotal };
}

/** Star rating as text. */
function Stars({ n }) {
  const full = Math.round(n || 0);
  return (
    <span style={{ color: '#ff9900', fontSize: 12 }} aria-label={`${n} of 5`}>
      {'★'.repeat(full)}
      <span style={{ color: '#d5d9dc' }}>{'★'.repeat(Math.max(0, 5 - full))}</span>
    </span>
  );
}

function ProductDetail({ sku, onBack, onAdd }) {
  const [p, setP] = useState(null);
  useEffect(() => {
    let alive = true;
    browse.product(sku).then((d) => alive && setP(d)).catch(() => alive && setP({ error: true }));
    return () => { alive = false; };
  }, [sku]);

  if (!p) return <Spinner />;
  if (p.error) return <Box color="text-status-error">Product not found.</Box>;

  return (
    <SpaceBetween size="m">
      <Button iconName="angle-left" variant="link" onClick={onBack}>Back to results</Button>
      <ColumnLayout columns={2}>
        <Box textAlign="center" fontSize="display-l" padding="xl">
          <div style={{ fontSize: 96 }}>{p.emoji || '📦'}</div>
        </Box>
        <SpaceBetween size="xs">
          <Box variant="h2">{p.title}</Box>
          <Box color="text-body-secondary">{p.brand} · {p.department}</Box>
          <SpaceBetween direction="horizontal" size="xs">
            <Stars n={p.rating} />
            <Box fontSize="body-s" color="text-body-secondary">{(p.ratingCount || 0).toLocaleString()} ratings</Box>
          </SpaceBetween>
          <Box variant="h1" color="text-status-error">${p.price}</Box>
          {p.prime && <Badge color="blue">✓ Free 1-day delivery</Badge>}
          <Button variant="primary" iconName="add-plus" onClick={() => onAdd(p)}>Add to cart</Button>
          <Box variant="awsui-key-label" margin={{ top: 's' }}>About this item</Box>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {(p.bullets || []).map((b, i) => <li key={i} style={{ fontSize: 13 }}>{b}</li>)}
          </ul>
        </SpaceBetween>
      </ColumnLayout>

      <Header variant="h3">Customer reviews</Header>
      <SpaceBetween size="xs">
        {(p.reviews || []).map((r, i) => (
          <Box key={i}>
            <SpaceBetween direction="horizontal" size="xs">
              <Stars n={r.stars} />
              <Box fontWeight="bold" fontSize="body-s">{r.title}</Box>
            </SpaceBetween>
            <Box fontSize="body-s" color="text-body-secondary">{r.author} · Verified Purchase</Box>
            <Box fontSize="body-s">{r.body}</Box>
          </Box>
        ))}
      </SpaceBetween>

      {p.related?.length > 0 && (
        <>
          <Header variant="h3">Related products</Header>
          <SpaceBetween direction="horizontal" size="xs">
            {p.related.slice(0, 6).map((r) => (
              <Box key={r.sku} textAlign="center" padding="xs">
                <div style={{ fontSize: 32 }}>{r.emoji || '📦'}</div>
                <Box fontSize="body-s">${r.price}</Box>
              </Box>
            ))}
          </SpaceBetween>
        </>
      )}
    </SpaceBetween>
  );
}

function CartView({ cart, onBack, onCheckout }) {
  return (
    <SpaceBetween size="m">
      <Button iconName="angle-left" variant="link" onClick={onBack}>Continue shopping</Button>
      <Header variant="h2">Shopping cart</Header>
      {cart.lines.length === 0 ? (
        <Box color="text-body-secondary">Your cart is empty. Add items, or let the agent shop for you.</Box>
      ) : (
        <SpaceBetween size="s">
          {cart.lines.map((l) => (
            <div key={l.product.sku} style={{ display: 'flex', gap: 12, alignItems: 'center', borderBottom: '1px solid #e9ebed', paddingBottom: 8 }}>
              <div style={{ fontSize: 40 }}>{l.product.emoji || '📦'}</div>
              <div style={{ flex: 1 }}>
                <Box fontWeight="bold">{l.product.title}</Box>
                <Box fontSize="body-s" color="text-body-secondary">{l.product.brand}</Box>
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="inline-icon" iconName="treeview-collapse" onClick={() => cart.setQty(l.product.sku, l.qty - 1)} ariaLabel="Decrease" />
                  <Box>{l.qty}</Box>
                  <Button variant="inline-icon" iconName="treeview-expand" onClick={() => cart.setQty(l.product.sku, l.qty + 1)} ariaLabel="Increase" />
                  <Button variant="inline-link" onClick={() => cart.remove(l.product.sku)}>Remove</Button>
                </SpaceBetween>
              </div>
              <Box variant="h3" color="text-status-error">${(parseFloat(l.product.price) * l.qty).toFixed(2)}</Box>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box variant="h3">Subtotal ({cart.count} items): <span style={{ color: '#b12704' }}>${cart.subtotal.toFixed(2)}</span></Box>
            <Button variant="primary" onClick={onCheckout}>Proceed to checkout</Button>
          </div>
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
}

function OrdersView({ orders, onBack }) {
  return (
    <SpaceBetween size="m">
      <Button iconName="angle-left" variant="link" onClick={onBack}>Continue shopping</Button>
      <Header variant="h2">Your orders</Header>
      {orders.length === 0 ? (
        <Box color="text-body-secondary">No orders yet — buy something, or ask the agent to.</Box>
      ) : (
        orders.map((o) => (
          <div key={o.id} style={{ border: '1px solid #e9ebed', borderRadius: 8, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box fontSize="body-s" color="text-body-secondary">ORDER #{o.id}</Box>
              <Badge color={o.placedBy === 'agent' ? 'blue' : 'grey'}>{o.placedBy === 'agent' ? '🤖 Bought by agent' : 'You bought'}</Badge>
            </div>
            <Box fontWeight="bold">Total ${o.total}</Box>
            {o.lines.map((l, i) => <Box key={i} fontSize="body-s">{l.emoji} {l.title} × {l.qty}</Box>)}
          </div>
        ))
      )}
    </SpaceBetween>
  );
}

export default function EmporiumStore({ reveals = [], store = { lines: [], orders: [] }, onStoreChange = () => {} }) {
  const [q, setQ] = useState('');
  const [dept, setDept] = useState({ label: 'All departments', value: 'All' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ name: 'home' }); // home | product | cart | orders
  const [toast, setToast] = useState(null);

  // Cart lines + orders live in the LIFTED, session-persisted store. Build stable
  // updater fns that patch only their slice so the parent snapshot stays in sync.
  const lines = store.lines || [];
  const orders = store.orders || [];
  const setLines = useCallback(
    (updater) => onStoreChange((s) => ({ ...s, lines: typeof updater === 'function' ? updater(s.lines || []) : updater })),
    [onStoreChange],
  );
  const setOrders = useCallback(
    (updater) => onStoreChange((s) => ({ ...s, orders: typeof updater === 'function' ? updater(s.orders || []) : updater })),
    [onStoreChange],
  );
  const cart = useCart(lines, setLines);

  const load = useCallback((query, department) => {
    setLoading(true);
    browse.catalog(query, department, 48)
      .then(setData)
      .catch(() => setData({ items: [], departments: [], total: 0 }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load('', 'All'); }, [load]);

  // Bridge AGENT activity into the storefront: when the agent buys a product or
  // assembles a shopping list, surface it as an agent-placed order (and seed the
  // list's picks into the cart). Idempotent by DETERMINISTIC order id — so a session
  // restore (which re-runs this over persisted reveals AND already-persisted orders)
  // never duplicates an order. Each reveal idx maps to a stable id.
  useEffect(() => {
    reveals.forEach((r, idx) => {
      const c = r.content || {};
      const kind = r.skillId || r.cardType;
      if ((kind === 'buy' || r.cardType === 'receipt') && c.confirmation) {
        const id = String(c.confirmation).replace(/^ORD-LOCAL-/, '');
        setOrders((o) => (o.some((x) => x.id === id) ? o : [{
          id,
          total: c.total || '—',
          placedBy: 'agent',
          lines: [{ title: c.ref || 'product', qty: 1, emoji: '🛍️' }],
        }, ...o]));
      } else if ((kind === 'list' || r.cardType === 'checklist') && Array.isArray(c.picks) && c.picks.length) {
        const id = `LIST-${idx + 1}`;
        setOrders((o) => {
          if (o.some((x) => x.id === id)) return o;
          // First time we see this list: also seed its picks into the cart.
          c.picks.forEach((p) => cart.add(p));
          return [{
            id,
            total: c.total || '—',
            placedBy: 'agent',
            lines: c.picks.map((p) => ({ title: p.title, qty: 1, emoji: p.emoji })),
          }, ...o];
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reveals]);

  const addToCart = (p) => {
    cart.add(p);
    setToast(`Added “${p.title}” to cart`);
    setTimeout(() => setToast(null), 1800);
  };
  const checkout = () => {
    const order = {
      id: String(1000 + orders.length + 1),
      total: cart.subtotal.toFixed(2),
      placedBy: 'you',
      lines: cart.lines.map((l) => ({ title: l.product.title, qty: l.qty, emoji: l.product.emoji })),
    };
    setOrders((o) => [order, ...o]);
    cart.clear();
    setView({ name: 'orders' });
  };

  const masthead = (
    <div style={{ background: '#131921', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
      <button onClick={() => setView({ name: 'home' })} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#fff', fontWeight: 800, fontSize: 18 }}>
        shop<span style={{ color: '#febd69' }}>agent</span>
      </button>
      <span style={{ color: '#cfd6dd', fontSize: 12, flex: 1 }}>Deliver to New York 10001</span>
      <button onClick={() => setView({ name: 'orders' })} style={mastheadBtn}>Orders{orders.length ? ` (${orders.length})` : ''}</button>
      <button onClick={() => setView({ name: 'cart' })} style={mastheadBtn}>🛒 Cart{cart.count ? ` (${cart.count})` : ''}</button>
    </div>
  );

  if (view.name === 'product') return <div>{masthead}<div style={{ marginTop: 12 }}><ProductDetail sku={view.sku} onBack={() => setView({ name: 'home' })} onAdd={addToCart} /></div></div>;
  if (view.name === 'cart') return <div>{masthead}<div style={{ marginTop: 12 }}><CartView cart={cart} onBack={() => setView({ name: 'home' })} onCheckout={checkout} /></div></div>;
  if (view.name === 'orders') return <div>{masthead}<div style={{ marginTop: 12 }}><OrdersView orders={orders} onBack={() => setView({ name: 'home' })} /></div></div>;

  const deptOptions = [{ label: 'All departments', value: 'All' }]
    .concat((data?.departments || []).map((d) => ({ label: d, value: d })));

  return (
    <SpaceBetween size="m">
      {masthead}
      {toast && <Alert type="success" dismissible onDismiss={() => setToast(null)}>{toast}</Alert>}

      <AgentRevealBanner reveals={reveals} accent="#febd69" />

      <SpaceBetween direction="horizontal" size="xs">
        <div style={{ flex: 1, minWidth: 200 }}>
          <Input
            value={q}
            placeholder="Search The Emporium…"
            type="search"
            onChange={({ detail }) => setQ(detail.value)}
            onKeyDown={({ detail }) => detail.key === 'Enter' && load(q, dept.value)}
          />
        </div>
        <Select
          selectedOption={dept}
          options={deptOptions}
          onChange={({ detail }) => { setDept(detail.selectedOption); load(q, detail.selectedOption.value); }}
        />
        <Button onClick={() => load(q, dept.value)}>Search</Button>
      </SpaceBetween>

      <Box fontSize="body-s" color="text-body-secondary">
        {loading ? 'Loading…' : `${data?.total?.toLocaleString() || 0} products`}
      </Box>

      {loading ? (
        <Spinner />
      ) : (data?.items || []).length === 0 ? (
        <Box textAlign="center" color="text-body-secondary">No products found.</Box>
      ) : (
        <div className="wafmon-grid">
          {data.items.map((p) => (
            <div key={p.sku} className="wafmon-card">
              <button className="wafmon-card-thumb" onClick={() => setView({ name: 'product', sku: p.sku })} aria-label={`View ${p.title}`}>
                {p.emoji || '📦'}
              </button>
              <button className="wafmon-card-title" onClick={() => setView({ name: 'product', sku: p.sku })}>
                {p.title}
              </button>
              <div className="wafmon-card-stars"><Stars n={p.rating} /></div>
              <div className="wafmon-card-price">${p.price}</div>
              <div className="wafmon-card-meta">{p.brand} · {p.department}</div>
              <Button fullWidth onClick={() => addToCart(p)}>Add to cart</Button>
            </div>
          ))}
        </div>
      )}
    </SpaceBetween>
  );
}

const mastheadBtn = {
  border: '1px solid #3a4553', background: 'transparent', color: '#fff',
  borderRadius: 6, padding: '4px 10px', fontSize: 13, cursor: 'pointer',
};
